import { FC, Fragment, useState } from "react";
import { Modal, Steps, Button, Space, Flex, InputNumber, Result, Spin, notification, Upload, UploadProps, Form, Input, Typography, App, Select } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/es/_util/type";
import { PlusOutlined, CheckCircleOutlined, DeleteOutlined, ExclamationCircleTwoTone } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { Product } from "@/types/product";
import { OrderV2, OrderAddV2, OrderAddItemV2, WLocation, InventoryV2, OrderItemV2 } from "@/types/inventoryV2";
import SText from "@/styles/SText";
import ProductSelectMulti from "@/components/Selector/ProductSelectMulti";
import { orderAdd, orderConfirm, orderDel, orderPreOut, realList } from "@/apis/inventoryv2";
import { ChangeEnum, ConfirmedEnum, OperateEnum } from "@/types/inventory";
import STableLayout from "@/styles/STableLayout";
import { generateTemplate, parseData } from "@/pages/Requirement/components/EditRequirement/utils";
import { getLocationIdx } from "@/utils/wLocation";
import OrderDetailV2 from "../../../Order/components/OrderDetailV2";
import { productList } from "@/apis/product";
import SelectLocationType from "@/components/Selector/SelectLocationType";
import { numFormmater } from "@/pages/Requirement/util";
import OrderConfirmInspection from "@/components/OrderConfirmInspection";
import { SpecialHandlingConfirm } from "@/types/inventoryV2";
interface OtherOutBoundProps {
  open: boolean;
  onCancel: () => void;
}

interface ProductWithNum extends Product {
  count: number;              // 出库数量，默认值1
  stockUseCount: number;     // 总可用库存数量
  locations?: Array<WLocation & {
    itemId: string;
    lot: string;
    count: number; //出库数量
    stockNumber: number; //库存数量
    stockPreIn: number;  //库存预入库
    stockPreOut: number; //库存预出库
    stockUseQty: number; //库存可用数量
  }>
}

const OtherOutBound: FC<OtherOutBoundProps> = ({ open, onCancel }) => {
  const { t } = useTranslation();
  const { modal } = App.useApp();

  const [current, setCurrent] = useState(0);
  const [products, setProducts] = useState<ProductWithNum[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [openProductSelect, setOpenProductSelect] = useState(false);

  const [addIng, setAddIng] = useState(false);
  const [orderResult, setOrderResult] = useState<OrderV2 | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [openCon, setOpenCon] = useState(false);
  const [openDel, setOpenDel] = useState(false);
  const [remark, setRemark] = useState<string>('');
  const [preOutLocationTypes, setPreOutLocationTypes] = useState<number[]>([1]);
  const [errorCodes, setErrorCodes] = useState<string[]>([]);
  const [openInspection, setOpenInspection] = useState(false);
  const [inspectionItems, setInspectionItems] = useState<OrderItemV2[]>([]);

  const [stockMap, setStockMap] = useState<Record<string, InventoryV2 & { useCount: number }>>({});

  const matchStock = async () => {
    if (products.length === 0) {
      notification.warning({
        message: t('warehouse.selectProduct'),
      });
      return;
    }
    let zeroPro = products.find(p => p.count === 0)
    if (zeroPro) {
      notification.warning({
        message: t('warehouse.outNumNotZ'),
        description: zeroPro.code
      })
      return
    }
    if (!!!preOutLocationTypes.length) {
      notification.warning({
        placement: 'top',
        message: '请选择出库匹配库位类型',
      });
      return;
    };

    setAddIng(true);
    try {
      let ids = products.map(pro => pro.id);
      let stocks = await realList({ body: { ids, locationTypes: preOutLocationTypes } });
      let mapData: Record<string, InventoryV2 & { useCount: number }> = {};
      (stocks.body ?? []).forEach(stock => mapData[stock.productId] = { ...stock, useCount: (stock.count ?? 0) - (stock.preOut ?? 0) });
      setStockMap({ ...mapData });
      setCurrent(1);
    } catch (error) {
    } finally {
      setAddIng(false);
    }
  };

  const addOrder = async (): Promise<void> => {
    setErrorCodes([]);
    let overCodes = products.filter(pro => pro.count > (stockMap[pro.id]?.useCount ?? 0)).map(p => p.code);
    if (overCodes.length > 0) {
      notification.error({ message: t('apiError.error31'), description: overCodes.join(',') })
      setErrorCodes([...overCodes]);
      return
    }
    setAddIng(true);
    let result: OrderV2 | null = null;
    try {
      // 构造 OrderAddV2 数据
      const orderData: OrderAddV2 = {
        type: ChangeEnum.PRE_OUT,
        opType: OperateEnum.TEMPORARY,
        desc: remark,
        preTime: null,
        arrivalAt: null,
        items: products.map(p => {
          const item: OrderAddItemV2 = {
            productId: p.id,
            count: p.count,
            lot: "",
            thdlot: "",
            desc: "",
            locations: [],
            sourceId: '',
          };
          return item;
        }),
      };

      result = await orderAdd(orderData);
      let preOutRes = await orderPreOut(result.id, preOutLocationTypes);
      setOrderResult(preOutRes);
      setCurrent(2);
    } catch (err: any) {
      result?.id && orderDel(result.id);
      if (err?.code === 73007 || err?.code === 73008) {
        if (Array.isArray(err?.extra)) {
          let errorCodes = products
            .filter(pro => err.extra.includes(pro.id) || err.extra.includes(pro.code))
            .map(pro => pro.code)
            .join(', ');
          notification.error({
            placement: 'top',
            message: t('apiError.error31'),
            description: errorCodes,
          });
        } else {
          notification.error({
            placement: 'top',
            message: t('warehouse.outboundError'),
            description: t('apiError.error31'),
          });
        }
      } else if (err?.code === 73010) {
        notification.error({
          message: t('inventory.proQtyFalse'),
          description: err?.extra?.map((i: { productId: string }) => i.productId)?.join(', ')
        })
      } else if ([73022, 73014].includes(err)) {
        notification.error({
          message: t('inventory.calcErr')
        })
      } else if ([73024, 73028].includes(err?.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: err?.extra?.productCode
        })
      } else {
        notification.error({
          placement: 'top',
          message: errorCodes[err] || t('warehouse.outboundError'),
          description: err + '',
        });
      }
    } finally {
      setAddIng(false);
    }
  };

  //第二步confirm
  const confirmOut = async (specialHandling: SpecialHandlingConfirm[]): Promise<void> => {
    setAddIng(true);
    try {
      if (!!orderResult) {
        await orderConfirm(orderResult.id, 0, specialHandling);
        // try {
        //   await orderConfirm(orderResult.id, 0, specialHandling);
        // } catch (error: any) {
        //   if (error?.code === 70845) await orderConfirm(orderResult.id, 0);
        //   else throw error
        // }
        setOrderResult({
          ...orderResult,
          confirmState: ConfirmedEnum.CONFIRM,
          confirmedAt: Date.now(),
        })
      }
      setCurrent(3);
      return;
    } catch (err: any) {
      if (err === 72811) {
        notification.error({
          placement: 'top',
          message: t('warehouse.outboundError'),
          description: t('product.dataError')
        });
      } else if (err === 73010) {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('warehouse.outFail'),
          description: t('warehouse.locFailed'),
        });
      } else if (err === 72809) {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('warehouse.outFail'),
          description: t('warehouse.needFix'),
        });
      } else if ([73024, 73028].includes(err.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: err?.extra?.productCode
        })
      } else notification.error({
        message: t('order.optErr'),
        description: err + '',
      });
      return;
    } finally {
      setAddIng(false);
    }
  };

  const handleNext = async () => {
    if (current === 0) {
      await matchStock();
      return;
    }

    if (current === 1) {
      await addOrder();
      return;
    }

    if (current === 2) {
      // 巡检弹窗关闭后由 onOk 触发确认，此处不直接 confirm
      return;
    } else {
      setCurrent(current + 1);
    }
  };

  // 确认弹窗确定后，打开巡检弹窗
  const handleConfirmModalOk = () => {
    setOpenCon(false);
    setInspectionItems(orderResult?.items || []);
    setOpenInspection(true);
  };

  const handleAddProducts = (addDatas: ProductWithNum[]) => {
    setProducts(prev => {
      // 创建code映射表
      const mapData: Record<string, ProductWithNum> = {};
      prev.forEach(d => mapData[d.code] = { ...d });

      // 处理addDatas
      addDatas.forEach(data => {
        if (mapData[data.code]) {
          // 存在相同code，累加count
          mapData[data.code] = {
            ...mapData[data.code],
            count: data.count + mapData[data.code].count
          };
        } else {
          // 不存在，直接添加
          mapData[data.code] = { ...data };
        }
      });

      // 将map的所有值转换为数组
      return Object.values(mapData);
    });
  };

  const handleDeleteSelected = () => {
    setErrorCodes(last => last.filter(code => !selectedRowKeys.includes(code)))
    setProducts(products.filter(p => !selectedRowKeys.includes(p.id)));
    setSelectedRowKeys([]);
  };

  // 处理出库数量变更
  const handleCountChange = (value: number | null, recordId: string) => {
    if (value !== null && value !== undefined) {
      setProducts(prev => prev.map(item => {
        if (item.id === recordId) {
          return { ...item, count: value };
        }
        return item;
      }));
    }
  };

  // 处理库位出库数量变更
  const handleLocationCountChange = (value: number | null, recordId: string, itemId: string) => {
    setProducts(prev => prev.map(item => {
      if (item.id === recordId && item.locations) {
        const newLocations = item.locations.map(loc => {
          if (loc.itemId === itemId) {
            return { ...loc, count: value || 0 };
          }
          return loc;
        });
        return { ...item, locations: newLocations };
      }
      return item;
    }));
  };

  // 统一列配置
  const columns: ColumnsType<ProductWithNum> = [
    {
      title: t("warehouse.productCode"),
      dataIndex: "code",
      key: "code",
      width: 120,
      fixed: "right",
      render: (text: string) => <SText style={{ width: 120, color: errorCodes.includes(text) ? "red" : "unset" }} text={text} />,
    },
    {
      title: t("warehouse.productName"),
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (text: string) => <SText style={{ width: 160 }} text={text} />,
    },
    {
      title: t("warehouse.productModel"),
      dataIndex: "model",
      key: "model",
      width: 120,
      ellipsis: true,
      render: (text: string) => <SText text={text} />,
    },
    {
      title: t("warehouse.uom"),
      dataIndex: "uom",
      key: "uom",
      width: 80,
      render: (text: string) => <SText text={text} />,
    },
    {
      title: t("warehouse.available"),
      dataIndex: "stockUseCount",
      key: "stockUseCount",
      width: 100,
      hidden: current !== 2,
      render: (value) => <SText text={String(value ?? 0)} />,
    },
    {
      title: t("bom.realStockNum"),
      key: "realStockNum",
      width: 100,
      hidden: current !== 1,
      render: (_, record) => <SText text={numFormmater(stockMap[record.id]?.count ?? 0)} />,
    },
    {
      title: t("bom.realPreStock"),
      key: "realPreStock",
      width: 140,
      hidden: current !== 1,
      render: (_, record) => <SText text={numFormmater(stockMap[record.id]?.preOut ?? 0)} />,
    },
    {
      title: t("bom.realUseStock"),
      key: "realUseStock",
      width: 140,
      hidden: current !== 1,
      render: (_, record) => <SText text={numFormmater(stockMap[record.id]?.useCount ?? 0)} />,
    },
    {
      title: t("warehouse.outNum"),
      dataIndex: "count",
      key: "count",
      width: 120,
      fixed: "right",
      render: (value, record: ProductWithNum) => (
        current > 1 ? <SText text={numFormmater(value)} /> : <InputNumber changeOnWheel
          formatter={val => numFormmater(val || 0)}
          value={value}
          min={0}
          // disabled={current > 0}
          max={current > 0 ? stockMap[record.id]?.useCount : undefined}
          onChange={(val) => handleCountChange(val, record.id)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("warehouse.editLocation"),
      dataIndex: "editLocation",
      key: "editLocation",
      width: 560,
      fixed: "right",
      hidden: current !== 2,
      render: (_, record: ProductWithNum) => {
        if (!record.locations || record.locations.length === 0) {
          return <SText text={t('warehouse.noStock')} />;
        }
        const totalCount = record.locations.reduce((sum, loc) => sum + loc.count, 0);
        return (
          <Flex vertical align="center" gap={4}>
            {totalCount > record.count && <SText style={{ color: "red" }} text={t('warehouse.outOvernum', { num: totalCount - record.count })} />}
            {totalCount < record.count && <SText style={{ color: "red" }} text={t('warehouse.outLossnum', { num: record.count - totalCount })} />}

            {record.locations.filter(loc => loc.stockUseQty > 0).map(loc => (
              <Flex style={{ width: "100%" }} gap={4}>
                <SText text={loc.lot} style={{ width: 260 }} />
                <InputNumber changeOnWheel
                  key={loc.id}
                  value={loc.count}
                  min={0}
                  max={loc.stockUseQty}
                  onChange={(val) => handleLocationCountChange(val, record.id, loc.itemId)}
                  style={{ flex: 1 }}
                  addonBefore={<SText text={getLocationIdx(loc)} style={{ minWidth: 80 }} />}
                  addonAfter={<SText text={`/ ${loc.stockUseQty}`} style={{ fontSize: 12, color: '#999' }} />}
                  status={totalCount > record.count ? "error" : undefined}
                />
              </Flex>
            ))}
          </Flex>
        );
      },
    },
  ];

  // 下载模版
  const handleSaveTemplate = () => {
    generateTemplate(t, t('warehouse.productInput'));
  };

  // 导入数据
  const handleImport: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    try {
      const addList = await parseData(file, t);
      if (addList.length > 0) {
        const productListRes = await productList({ params: { strs: addList.map(a => a.product.code) } });
        const productListMap: Record<string, Product> = {};
        productListRes.body.forEach(p => {
          productListMap[p.code] = { ...p }
        });
        let notProsList: string[] = [];
        for (let index = 0; index < addList.length; index++) {
          let code = addList[index].product.code
          if (!!productListMap[code]) {
            addList[index].product = { ...productListMap[code] }
          } else notProsList.push(code)
        }
        if (notProsList.length > 0) {
          setErrorCodes(notProsList)
          notification.error({
            message: t('apiError.error26'),
            description: notProsList.join(', ')
          })
        }
        handleAddProducts(addList.map(pro => ({
          ...pro.product,
          count: pro.useAmount,   //出库数量
          stockUseCount: 0,
        })))
        notification.success({ placement: 'top', message: t("requirement.parseDataSuccess", { count: addList.length }) });
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("requirement.invalidTemplate") });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };

  const closeModal = () => {
    if (current !== 2 && orderResult && orderResult.confirmState !== ConfirmedEnum.CONFIRM) setOpenDel(true);
    else onCancel();
  };

  const delClose = async () => {
    setOpenDel(false);
    try {
      setAddIng(true);
      await orderDel(orderResult?.id ?? "");
      notification.success({
        message: t('common.deleteSuccess'),
        description: orderResult?.number
      })
      onCancel();
    } catch (error) {
      notification.error({
        message: t('order.optErr')
      })
    }
  };

  const headerChildren = (
    <Flex gap={10} justify="space-between">
      {current === 0 && (
        <>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setOpenProductSelect(true)}
            disabled={current > 0}
          >
            {t("warehouse.addProduct")}
          </Button>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={handleImport}
            showUploadList={false}
            disabled={uploading}
          >
            <Button loading={uploading}>{t("requirement.inputTemp")}</Button>
          </Upload>
          <Button
            onClick={handleSaveTemplate}
          >
            {t('requirement.downloadTemplate')}
          </Button>
        </>
      )}
      <Button
        danger
        icon={<DeleteOutlined />}
        onClick={handleDeleteSelected}
        disabled={selectedRowKeys.length === 0}
      >
        {t("warehouse.delete")}
      </Button>
    </Flex>
  );

  const steps = [
    {
      title: t("warehouse.selectProduct"),
      content: (
        <STableLayout
          headerChildren={headerChildren}
          headerSub={
            <Flex vertical style={{ flex: 1 }}>
              <Form.Item required label={t('requirement.matchLocType')} style={{ marginBottom: 8, width: "100%" }}>
                <SelectLocationType
                  mode="multiple"
                  style={{ flex: 1 }}
                  value={preOutLocationTypes}
                  onChange={setPreOutLocationTypes}
                  maxTagCount={2}
                />
              </Form.Item>
              <Form.Item label={t('purchase.remark')} style={{ marginBottom: 24 }}>
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} value={remark} onChange={e => setRemark(e.target.value)} maxLength={500} showCount />
              </Form.Item>
            </Flex>
          }
          total={products.length}
          handleChange={() => { }}
          columns={columns as ColumnsType<AnyObject>}
          dataSource={products}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
          }}
          pagination={false}
          extHeight={100}
        />
      ),
    },
    {
      title: t("requirement.matchInventory"),
      content: (
        <STableLayout
          headerChildren={null}
          total={products.length}
          handleChange={() => { }}
          columns={columns as ColumnsType<AnyObject>}
          dataSource={products}
          pagination={false}
          extHeight={100}
        />
      ),
    },
    {
      title: t("requirement.preOut"),
      content: (
        <Fragment>
          <Typography.Title level={4} style={{ marginTop: 0 }} >{orderResult?.number}</Typography.Title>
          <OrderDetailV2 noModal data={orderResult || undefined} handleCancel={() => { }} handleRm={() => { }} />
        </Fragment>

      ),
    },
    {
      title: t("warehouse.complete"),
      content: (
        <div style={{ minHeight: "400px", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title={orderResult ? `${orderResult.number} ${t('warehouse.outboundSuccess')}` : t('warehouse.outboundSuccess')}
            subTitle={t('inventory.outboundSuccess')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={t("warehouse.otherOut")}
        open={open}
        onCancel={closeModal}
        width={1200}
        centered
        footer={
          <Flex justify="space-between" style={{ width: "100%" }}>
            <div>
              <Space>
                {current === 0 && (
                  <Button type="primary" disabled={products.length === 0} loading={addIng} onClick={handleNext} >
                    {t("warehouse.next")}
                  </Button>
                )}
                {current === 1 && (
                  <Button onClick={() => setCurrent(0)} >
                    {t("warehouse.previous")}
                  </Button>
                )}
                {current === 1 && (
                  <Button type="primary" loading={addIng} onClick={handleNext} >
                    {t("warehouse.next")}
                  </Button>
                )}
                {current === 2 && (
                  <>
                    {/* <Button onClick={handlePrev}>{t("warehouse.previous")}</Button> */}
                    <Button type="primary" loading={addIng} onClick={() => setOpenCon(true)}>
                      {t("warehouse.next")}
                    </Button>
                  </>
                )}
                {current === 3 && <Button type="primary" onClick={onCancel}>
                  {t("warehouse.confirm")}
                </Button>}
              </Space>
            </div>
          </Flex>
        }
      >
        <Spin spinning={addIng}>
          <Steps current={current} items={steps.map(item => ({ title: item.title }))} style={{ marginBottom: 24 }} />
          <Flex vertical style={{ width: "100%", height: "70vh", overflow: "hidden" }}>
            {steps[current].content}
          </Flex>
        </Spin>
      </Modal>

      <ProductSelectMulti
        openProduct={openProductSelect}
        setOpenProduct={setOpenProductSelect}
        handleOk={res => handleAddProducts(res.map(r => ({ ...r, count: 0, stockUseCount: 0 })))}
        isQuantity={false}
      />
      <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } open={openCon} onOk={handleConfirmModalOk} onCancel={() => setOpenCon(false)}>
        {t('warehouse.confirmOutboundTip')}
      </Modal>
      <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } open={openDel} onOk={() => { delClose(); }} onCancel={() => { setOpenDel(false); onCancel() }}
        okText={t('warehouse.delOrderClose')}
        cancelText={t('warehouse.notDelClose')}
        okButtonProps={{ danger: true }}
      >
        {t('warehouse.delOutTip', { number: orderResult?.number })}
      </Modal>
      {/* 巡检弹窗：onOk 后执行出库确认逻辑 */}
      <OrderConfirmInspection
        items={inspectionItems}
        open={openInspection}
        onOk={(data) => {
          setOpenInspection(false);
          confirmOut(data);
        }}
        onClose={() => setOpenInspection(false)}
      />
    </>
  );
};

export default OtherOutBound;
